import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { SidebarModule } from 'primeng/sidebar';
import { ErrorReportingService } from '../../services/error-reporting/error-reporting.service';
import { RealEstateLocationSuggestion, RealEstateLocationsService } from '../../services/real-estate-locations/real-estate-locations.service';

@Component({
  selector: 'app-real-estate-search-sidebar',
  standalone: true,
  imports: [SidebarModule],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './real-estate-search-sidebar.component.html',
  styleUrl: './real-estate-search-sidebar.component.scss',
})
export class RealEstateSearchSidebarComponent implements OnChanges, OnDestroy {
  @Input() query = '';
  @Output() queryChange = new EventEmitter<string>();
  @Output() submit = new EventEmitter<RealEstateLocationSuggestion>();
  @ViewChild('searchInput') private searchInput: ElementRef<HTMLInputElement>;
  isVisible = false;
  protected suggestions: RealEstateLocationSuggestion[] = [];
  protected openSuggestions = false;
  protected activeIndex = -1;
  protected selectedSuggestion: RealEstateLocationSuggestion | null = null;
  private timer?: ReturnType<typeof setTimeout>;
  private requestId = 0;
  private suggestionsTerm = '';

  constructor(
    private errorReportingService: ErrorReportingService,
    private locationsService: RealEstateLocationsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['query'] && this.selectedSuggestion?.label !== this.query) {
      this.selectedSuggestion = null;
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.timer);
    this.requestId++;
  }

  close(): void {
    this.isVisible = false;
    this.openSuggestions = false;
    clearTimeout(this.timer);
    this.requestId++;
  }

  isOpen(): boolean {
    return this.isVisible;
  }

  open(): void {
    this.isVisible = true;
    setTimeout(() => this.searchInput?.nativeElement.focus(), 150);
  }

  protected clearQuery(): void {
    this.changeQuery('');
  }

  protected onFocus(): void {
    if (this.suggestions.length && this.suggestionsTerm === this.query.trim()) {
      this.openSuggestions = true;
    } else if (this.query.trim()) {
      this.loadSuggestions();
    }
  }

  protected onInput(event: Event): void {
    this.changeQuery((event.target as HTMLInputElement).value);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      clearTimeout(this.timer);
      this.requestId++;
      this.openSuggestions = false;
    } else if (event.key === 'ArrowDown' && this.suggestions.length) {
      event.preventDefault();
      this.openSuggestions = true;
      this.activeIndex = Math.min(this.activeIndex + 1, this.suggestions.length - 1);
    } else if (event.key === 'ArrowUp' && this.openSuggestions) {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    } else if (event.key === 'Enter') {
      if (this.openSuggestions && this.activeIndex >= 0) {
        event.preventDefault();
        this.select(this.suggestions[this.activeIndex]);
      } else if (this.selectedSuggestion) {
        event.preventDefault();
        this.search();
      }
    }
  }

  protected search(): void {
    if (this.selectedSuggestion) {
      this.submit.emit(this.selectedSuggestion);
      this.close();
    }
  }

  protected select(suggestion: RealEstateLocationSuggestion): void {
    clearTimeout(this.timer);
    this.requestId++;
    this.query = suggestion.label;
    this.queryChange.emit(this.query);
    this.selectedSuggestion = suggestion;
    this.openSuggestions = false;
    this.suggestions = [];
    this.activeIndex = -1;
  }

  private changeQuery(query: string): void {
    this.query = query;
    this.queryChange.emit(query);
    this.selectedSuggestion = null;
    this.loadSuggestions();
  }

  private loadSuggestions(): void {
    this.suggestions = [];
    this.activeIndex = -1;
    this.openSuggestions = false;
    clearTimeout(this.timer);
    const requestId = ++this.requestId;
    const term = this.query.trim();
    if (!term) {
      return;
    }
    this.timer = setTimeout(async () => {
      try {
        const suggestions = await this.locationsService.search({ limit: 10, term });
        if (requestId === this.requestId) {
          this.suggestions = suggestions;
          this.suggestionsTerm = term;
          this.openSuggestions = this.isVisible;
        }
      } catch (error) {
        this.errorReportingService.log('RealEstateSearchSidebarComponent.loadSuggestions()', 'search', error);
        if (requestId === this.requestId) {
          this.suggestions = [];
          this.openSuggestions = false;
        }
      }
    }, 250);
  }
}
